// PRISAI Authentication Utilities
// SECURE VERSION - No auto-login, require explicit authentication

import type { User } from '@/types';

// Cookie utilities for session management only
export function setCookie(name: string, value: string, days: number = 7): void {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict;HttpOnly`;
}

export function getCookie(name: string): string | null {
  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1);
    }
    if (cookie.indexOf(nameEQ) === 0) {
      return cookie.substring(nameEQ.length);
    }
  }
  return null;
}

export function deleteCookie(name: string): void {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/`;
}

// Auth state management - SECURE VERSION
// We do NOT persist auth state to localStorage to prevent auto-login
// Auth state is only held in memory during the session

export interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  apiKey: string | null;
}

// This is ONLY for temporary in-memory auth tracking
// NO localStorage persistence - that was the security bug!
let inMemoryAuth: AuthState = {
  isAuthenticated: false,
  user: null,
  apiKey: null,
};

// Clear auth state
export function clearAuthState(): void {
  inMemoryAuth = { isAuthenticated: false, user: null, apiKey: null };
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem('session_token');
    localStorage.removeItem('api_key');
  }
}

// Set auth state in memory only
export function setAuthState(state: AuthState): void {
  inMemoryAuth = state;
}

// Get auth state from memory only
export function getAuthState(): AuthState {
  return inMemoryAuth;
}

// Role checking
export function isAdmin(user: User | null): boolean {
  return user?.role === 'admin';
}

export function hasPermission(user: User | null, requiredRole: 'admin' | 'user'): boolean {
  if (!user) return false;
  if (requiredRole === 'admin') return user.role === 'admin';
  return true;
}
